import Phaser from 'phaser';
import { BYGG_INTRO_VIDEO, FONTS, GAME_WIDTH, GAME_HEIGHT, fontStr } from '@/config';

const FADE_UT_MS = 500;

export class BygningIntro {
  private aktiv = false;
  private ferdig = false;
  private videoEl?: HTMLVideoElement;
  private container?: HTMLDivElement;
  private fadeLag?: Phaser.GameObjects.Rectangle;
  private hoppKnapp?: Phaser.GameObjects.Text;
  private hoppOverlay?: Phaser.GameObjects.Rectangle;
  private hoppOverTaster: Phaser.Input.Keyboard.Key[] = [];
  private onDoneCb?: () => void;
  private timerEv?: Phaser.Time.TimerEvent;

  constructor(private scene: Phaser.Scene) {}

  spill(onDone: () => void): void {
    if (this.aktiv) {
      onDone();
      return;
    }
    this.aktiv = true;
    this.ferdig = false;
    this.onDoneCb = onDone;

    const canvas = this.scene.game.canvas;
    const parent = canvas.parentElement;
    if (!parent) {
      this.avslutt();
      return;
    }

    parent.style.position = parent.style.position || 'relative';

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.inset = '0';
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.background = '#000';
    container.style.zIndex = '40';
    container.style.transition = 'opacity 500ms ease-out';
    container.style.opacity = '1';
    parent.appendChild(container);
    this.container = container;

    const video = document.createElement('video');
    video.src = BYGG_INTRO_VIDEO;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'cover';
    container.appendChild(video);
    this.videoEl = video;

    video.addEventListener('ended', () => this.startFadeUt(), { once: true });
    video.addEventListener('error', () => this.avslutt(), { once: true });
    void video.play().catch(() => this.avslutt());

    this.timerEv = this.scene.time.delayedCall(8000, () => this.startFadeUt());

    this.tilbyHoppOver();
  }

  private tilbyHoppOver(): void {
    this.hoppOverlay = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0)
      .setOrigin(0, 0)
      .setDepth(2090)
      .setInteractive();
    this.hoppOverlay.on('pointerup', () => this.hoppOver());

    this.hoppKnapp = this.scene.add
      .text(GAME_WIDTH - 40, 40, 'Hopp over ›', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(32),
        color: '#ffffff',
        backgroundColor: 'rgba(0,0,0,0.45)',
        padding: { left: 16, right: 16, top: 8, bottom: 8 },
      })
      .setOrigin(1, 0)
      .setDepth(2095)
      .setInteractive({ useHandCursor: true });
    this.hoppKnapp.on('pointerup', () => this.hoppOver());

    const keyboard = this.scene.input.keyboard;
    if (keyboard) {
      const space = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
      const esc = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
      space.on('down', () => this.hoppOver());
      esc.on('down', () => this.hoppOver());
      this.hoppOverTaster.push(space, esc);
    }
  }

  private hoppOver(): void {
    if (this.ferdig) return;
    this.startFadeUt();
  }

  private startFadeUt(): void {
    if (this.ferdig) return;
    this.timerEv?.remove(false);
    this.timerEv = undefined;

    if (this.container) {
      this.container.style.opacity = '0';
    }

    this.scene.time.delayedCall(FADE_UT_MS, () => this.avslutt());
  }

  private avslutt(): void {
    if (this.ferdig) return;
    this.ferdig = true;

    if (this.videoEl) {
      try {
        this.videoEl.pause();
      } catch {
        // ignore
      }
      this.videoEl.removeAttribute('src');
      this.videoEl.load();
      this.videoEl = undefined;
    }
    this.container?.remove();
    this.container = undefined;

    this.fadeLag?.destroy();
    this.fadeLag = undefined;
    this.hoppKnapp?.destroy();
    this.hoppKnapp = undefined;
    this.hoppOverlay?.destroy();
    this.hoppOverlay = undefined;

    for (const k of this.hoppOverTaster) k.removeAllListeners();
    this.hoppOverTaster = [];

    this.aktiv = false;
    const cb = this.onDoneCb;
    this.onDoneCb = undefined;
    cb?.();
  }
}
